import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, DialogHeader, DialogTitle } from "@sportscanner/ui";
import { apiGet, apiPatch, getApiErrorCode } from "@/lib/api";
import DateSlider from "./date-slider";
import {
  generateDates,
  dateStrToEntry,
  formatCourtLabel,
  type DateEntry,
} from "./helpers";

interface CourtSummary {
  id: string;
  name: string;
  sportId: string;
  pricePerSession: string;
}

interface SportAvailability {
  sportId: string;
  sportName: string;
  sessionDurationMinutes: number;
  courtsHaveSameSchedule: boolean;
  dates: Record<string, Record<string, string[]>>;
}

interface ClosureSummary {
  date: string;
  reason: string;
}

interface AvailabilityResponse {
  clubId: string;
  courts: CourtSummary[];
  closures?: ClosureSummary[];
  availability: SportAvailability[];
}

interface Props {
  clubId: string;
  bookingId: string;
  sportId: string;
  currentCourtId: string;
  currentDate: string;
  currentStartTime: string;
  onBack: () => void;
  onRescheduled?: () => void;
}

interface SlotOption {
  time: string;
  courtId: string;
  courtName: string;
}

// Rendered as a step inside BookingDetailsDialog (not a separate dialog) so
// admins don't end up with a dialog-in-dialog stack. `onBack` returns the user
// to the details view.
export default function RescheduleStep({
  clubId,
  bookingId,
  sportId,
  currentCourtId,
  currentDate,
  currentStartTime,
  onBack,
  onRescheduled,
}: Props) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [selected, setSelected] = useState<SlotOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    setSelectedDate(currentDate);
    setSelected(null);
    setSubmitError(null);
  }, [currentDate]);

  // 60-day window (mirrors what create dialog requests) so admins can move
  // bookings a couple months ahead without changing the page.
  const query = useQuery({
    queryKey: ["admin", "availability", clubId, "reschedule"],
    queryFn: () =>
      apiGet<AvailabilityResponse>(
        `/api/admin/clubs/${clubId}/availability?days=60`,
      ),
    staleTime: 30_000,
  });

  const dates: DateEntry[] = useMemo(() => generateDates(60), []);
  const availability = query.data;

  const closedDates = useMemo(() => {
    if (!availability?.closures) return new Set<string>();
    return new Set(availability.closures.map((c) => c.date));
  }, [availability]);

  const slots: SlotOption[] = useMemo(() => {
    if (!availability) return [];
    const sport = availability.availability.find((a) => a.sportId === sportId);
    if (!sport) return [];
    const day = sport.dates[selectedDate];
    if (!day) return [];
    const courtsById = new Map(availability.courts.map((c) => [c.id, c]));
    const list: SlotOption[] = [];
    for (const [courtId, times] of Object.entries(day)) {
      const court = courtsById.get(courtId);
      if (!court) continue;
      for (const time of times) {
        if (
          courtId === currentCourtId &&
          selectedDate === currentDate &&
          time === currentStartTime
        ) {
          continue;
        }
        list.push({ time, courtId, courtName: court.name });
      }
    }
    list.sort(
      (a, b) =>
        a.time.localeCompare(b.time) || a.courtName.localeCompare(b.courtName),
    );
    return list;
  }, [
    availability,
    selectedDate,
    sportId,
    currentCourtId,
    currentDate,
    currentStartTime,
  ]);

  const dateEntry = useMemo(() => dateStrToEntry(selectedDate), [selectedDate]);

  const submit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await apiPatch(
        `/api/admin/clubs/${clubId}/bookings/${bookingId}/reschedule`,
        {
          courtId: selected.courtId,
          date: selectedDate,
          startTime: selected.time,
        },
      );
      toast.success(t("bookings.reschedule.toast"));
      onRescheduled?.();
      onBack();
    } catch (err) {
      const code = getApiErrorCode(err);
      if (code === "SLOT_TAKEN") {
        setSubmitError(t("bookings.reschedule.slotTaken"));
        void query.refetch();
        setSelected(null);
      } else {
        setSubmitError(t(`errors.api.${code}`));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={onBack}
        disabled={submitting}
        className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-700 transition-colors cursor-pointer disabled:opacity-50"
      >
        <ChevronLeft className="w-4 h-4" />
        {t("bookings.form.back")}
      </button>

      <DialogHeader>
        <DialogTitle>{t("bookings.reschedule.title")}</DialogTitle>
      </DialogHeader>

      <p className="text-sm text-neutral-500">
        {t("bookings.reschedule.description")}
      </p>

      {query.isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : query.isError || !availability ? (
        <p className="text-sm text-red-500 text-center py-4">
          {t("bookings.reschedule.loadError")}
        </p>
      ) : (
        <div className="space-y-4 min-w-0">
          <DateSlider
            dates={dates}
            selectedDate={selectedDate}
            onSelectDate={(d) => {
              setSelectedDate(d);
              setSelected(null);
            }}
          />

          <div className="max-h-72 overflow-y-auto rounded-lg border border-neutral-200">
            {closedDates.has(selectedDate) ? (
              <p className="text-sm text-neutral-500 text-center py-6 px-4">
                {t("bookings.reschedule.clubClosed")}
              </p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-neutral-500 text-center py-6 px-4">
                {t("bookings.reschedule.noSlots", {
                  date: `${dateEntry.date}.`,
                })}
              </p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {slots.map((s) => {
                  const isSelected =
                    selected?.courtId === s.courtId &&
                    selected?.time === s.time;
                  return (
                    <li key={`${s.courtId}-${s.time}`}>
                      <button
                        type="button"
                        onClick={() => setSelected(s)}
                        className={`w-full text-left px-4 py-3 text-sm flex justify-between items-center transition-colors cursor-pointer ${
                          isSelected
                            ? "bg-primary-50 text-primary-700"
                            : "hover:bg-neutral-50 text-neutral-800"
                        }`}
                      >
                        <span className="font-medium">{s.time}</span>
                        <span className="text-neutral-500">
                          {formatCourtLabel(
                            s.courtName,
                            t("bookings.details.court"),
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {submitError && <p className="text-sm text-red-500">{submitError}</p>}
        </div>
      )}

      <Button
        onClick={submit}
        disabled={!selected || submitting}
        className="w-full rounded-full bg-primary-600 hover:bg-primary-700"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          t("bookings.reschedule.submit")
        )}
      </Button>
    </>
  );
}
